/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';

import { AxiosPromise, AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useTheme } from '@mui/material';

import { useHandleError } from '@europrocurement/l2d-hooks';
import {
    useModal,
    ConfirmationModal,
    PromiseConfirmationModal,
    type ConfirmationModalProps,
    type PromiseConfirmationModalProps,
} from '@europrocurement/flexy-components';

/**
 * Paramètres par défaut pour les requêtes API
 */
type DefaultParameters = {
    /** Afficher des notifications toast */
    withToaster: boolean;
    /** Appliquer automatiquement le type MIME pour PATCH */
    autoPatchMimeType: boolean;
};

/**
 * Paramètres additionnels pour les requêtes API
 */
export type AdditionalParameters = {
    /** Afficher des notifications toast */
    withToaster?: boolean;
    /** Message de succès personnalisé à afficher dans le toast */
    successMessage?: string;
    /** Message d'erreur personnalisé à afficher dans le toast */
    errorMessage?: string;
    /** Callback à exécuter en cas de succès */
    successCallback?: (result: AxiosResponse<any, any>) => unknown;
    /** Callback à exécuter en cas d'échec */
    failureCallback?: (error: unknown) => unknown;
    /** Callback à exécuter à la fin de la requête (succès ou échec) */
    finallyCallback?: (...args: unknown[]) => unknown;
    /** En-têtes personnalisés à ajouter à la requête */
    headers?: Record<string, string>;
    /** Appliquer automatiquement le type MIME pour PATCH */
    autoPatchMimeType?: boolean;
    /** Confirmer avant d'effectuer l'action */
    confirmationModal?: boolean;
    /** Props pour la modal de confirmation */
    confirmationModalProps?: Omit<
        ConfirmationModalProps,
        'actionOnValidation' | 'actionOnCancellation'
    >;
    /** Utiliser une modal de confirmation qui supporte les promesses */
    confirmationPromise?: boolean;
    /** Props pour la modal de confirmation avec promesse */
    confirmationPromiseProps?: Omit<PromiseConfirmationModalProps, 'onConfirm'>;
};

/**
 * État pour suivre le statut de la requête API
 */
type RequestState<ResponseType> = {
    /** Données de réponse de l'API */
    response: ResponseType;
    /** Si la requête a résulté en erreur */
    error: boolean;
    /** Si la requête a réussi */
    success: boolean;
    /** Si la requête est en cours de traitement */
    isProcessing: boolean;
    /** Si la requête est terminée (succès ou erreur) */
    isLoaded: boolean;
    /** Si la ressource n'a pas été trouvée (404) */
    notFound: boolean;
};

/**
 * Paramètres combinés (défaut + additionnels)
 */
type Parameters = DefaultParameters & AdditionalParameters;

/**
 * Type pour la fonction ou la promesse de requête
 */
type RequestInput = AxiosPromise | (() => AxiosPromise);

/**
 * Hook personnalisé pour effectuer des requêtes API avec gestion standardisée des erreurs
 * et suivi de l'état.
 *
 * @returns Objet contenant la fonction de requête et l'état actuel de la requête
 */
function useApiRequestControllable<ResponseType>() {
    const theme = useTheme();
    const initialState = {
        error: false,
        success: false,
        response: null as unknown as ResponseType,
        notFound: false,
        isLoaded: false,
        isProcessing: false,
    };

    const [requestState, _setRequestState] =
        React.useState<RequestState<ResponseType>>(initialState);
    const { enqueueSnackbar } = useSnackbar();
    const { handleFailure } = useHandleError();
    const { modalActions } = useModal();

    /**
     * Met à jour une propriété unique dans l'état de la requête
     */
    const setRequestState = React.useCallback(
        (key: string, value: unknown) =>
            _setRequestState((prevState) => ({
                ...prevState,
                [key]: value,
            })),
        [],
    );

    /**
     * Paramètres par défaut pour toutes les requêtes
     */
    const defaultParameters = React.useMemo(
        () => ({
            withToaster: true,
            autoPatchMimeType: true,
        }),
        [],
    );

    /**
     * Codes de statut HTTP considérés comme réussis
     */
    const successStatuses = React.useMemo(() => [200, 201, 204], []);

    /**
     * Génère un message de succès par défaut basé sur le code de statut HTTP
     */
    const getDefaultSuccessMessage = React.useCallback((statusCode: number) => {
        let prefix = '';
        switch (statusCode) {
            case 200:
                prefix = 'Mise à jour';
                break;
            case 201:
                prefix = 'Création';
                break;
            case 204:
                prefix = 'Suppression';
                break;
            default:
                prefix = 'Opération';
        }
        return `${prefix} effectuée avec succès`;
    }, []);

    /**
     * Gère la réponse API réussie
     */
    const handleSuccess = React.useCallback(
        (result: AxiosResponse<any, any>, parameters: Parameters) => {
            const { withToaster, successCallback, successMessage } = parameters;
            setRequestState('success', true);
            setRequestState('response', result.data);
            if (successCallback) {
                successCallback(result);
            }
            if (withToaster) {
                const message = successMessage || getDefaultSuccessMessage(result.status);
                enqueueSnackbar(message, { variant: 'success' });
            }
        },
        [enqueueSnackbar, getDefaultSuccessMessage, setRequestState],
    );

    /**
     * Vérifie si une requête est une méthode PATCH
     */
    const isPatchRequest = React.useCallback((request: AxiosPromise): boolean => {
        const anyRequest = request as any;
        if (anyRequest.config && anyRequest.config.method) {
            return anyRequest.config.method.toLowerCase() === 'patch';
        }
        return false;
    }, []);

    /**
     * Ajoute le type MIME PATCH à la requête si nécessaire
     */
    const addPatchMimeType = React.useCallback(
        (request: AxiosPromise, parameters: Parameters): AxiosPromise => {
            if (!parameters.autoPatchMimeType || !isPatchRequest(request)) {
                return request;
            }
            // Opération mutable pour modifier la configuration
            const anyRequest = request as any;
            if (anyRequest.config) {
                if (!anyRequest.config.headers) {
                    anyRequest.config.headers = {};
                }
                anyRequest.config.headers['Content-Type'] = 'application/patch+json+ld';
            }
            return request;
        },
        [isPatchRequest],
    );

    /**
     * Ajoute des en-têtes personnalisés à la requête
     */
    const addCustomHeaders = React.useCallback(
        (request: AxiosPromise, parameters: Parameters): AxiosPromise => {
            if (!parameters.headers) {
                return request;
            }
            // Opération mutable pour modifier la configuration
            const anyRequest = request as any;
            if (anyRequest.config) {
                if (!anyRequest.config.headers) {
                    anyRequest.config.headers = {};
                }
                Object.entries(parameters.headers).forEach(([key, value]) => {
                    anyRequest.config.headers[key] = value;
                });
            }
            return request;
        },
        [],
    );

    /**
     * Exécute la requête API réelle
     */
    const executeRequest = React.useCallback(
        async (preparedRequest: AxiosPromise, parameters: Parameters) => {
            try {
                const result = await preparedRequest;
                if (successStatuses.includes(result.status)) {
                    handleSuccess(result, parameters);
                } else {
                    console.error('CODE RESPONSE NOT SUPPORTED:', result.status);
                    setRequestState('error', true);
                }
                return result;
            } catch (error) {
                setRequestState('error', true);
                setRequestState('notFound', (error as any).statusCode === 404);
                handleFailure({
                    error,
                    withToaster: parameters.withToaster,
                    errorMessage: parameters.errorMessage,
                    failureCallback: parameters.failureCallback,
                });
                throw error;
            } finally {
                if (parameters.finallyCallback) {
                    parameters.finallyCallback();
                }
                setRequestState('isProcessing', false);
                setRequestState('isLoaded', true);
            }
        },
        [handleFailure, handleSuccess, setRequestState, successStatuses],
    );

    /**
     * Prépare et exécute la requête AxiosPromise
     */
    const prepareAndExecuteRequest = React.useCallback(
        async (requestInput: RequestInput, parameters: Parameters) => {
            // Obtient la requête AxiosPromise
            const req = typeof requestInput === 'function' ? requestInput() : requestInput;

            // Prépare la requête avec les en-têtes appropriés
            let preparedRequest = req;
            preparedRequest = addPatchMimeType(preparedRequest, parameters);
            preparedRequest = addCustomHeaders(preparedRequest, parameters);

            // Met à jour les états avant exécution
            setRequestState('isProcessing', true);
            setRequestState('isLoaded', false);
            setRequestState('error', false);

            // Exécute la requête préparée
            return executeRequest(preparedRequest, parameters);
        },
        [addCustomHeaders, addPatchMimeType, executeRequest, setRequestState],
    );

    /**
     * Effectue une requête API avec gestion standardisée des erreurs
     */
    const request = React.useCallback(
        async (requestInput: RequestInput, additionalParameters: AdditionalParameters = {}) => {
            const parameters = { ...defaultParameters, ...additionalParameters };

            // Si confirmation avec promesse est requise
            if (parameters.confirmationPromise) {
                return new Promise<AxiosResponse>((resolve, reject) => {
                    modalActions.call(
                        <PromiseConfirmationModal
                            color={theme.palette.primary}
                            {...parameters.confirmationPromiseProps}
                            onConfirm={async () => {
                                try {
                                    const result = await prepareAndExecuteRequest(
                                        requestInput,
                                        parameters,
                                    );
                                    resolve(result);
                                    modalActions.close();
                                } catch (error) {
                                    reject(error);
                                }
                            }}
                            actionOnCancellation={() => {
                                modalActions.close();
                                reject(new Error('User cancelled the operation'));
                            }}
                            isFullWidth
                        />,
                        true,
                        false,
                    );
                });
            }

            // Si une confirmation est requise, affiche la modale de confirmation
            if (parameters.confirmationModal) {
                await new Promise<void>((resolve, reject) => {
                    modalActions.call(
                        <ConfirmationModal
                            color={theme.palette.primary}
                            {...parameters.confirmationModalProps}
                            actionOnValidation={() => {
                                modalActions.close();
                                resolve();
                            }}
                            actionOnCancellation={() => {
                                modalActions.close();
                                reject(new Error('User cancelled the operation'));
                            }}
                        />,
                        true,
                        true,
                    );
                });

                // Après confirmation, prépare et exécute la requête
                return prepareAndExecuteRequest(requestInput, parameters);
            }
            // Pas de confirmation nécessaire, prépare et exécute directement
            return prepareAndExecuteRequest(requestInput, parameters);
        },
        [defaultParameters, modalActions, prepareAndExecuteRequest, theme.palette.primary],
    );

    return { request, requestState };
}

export default useApiRequestControllable;
